import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import {
  IAppPluginWidget,
  IAppDashboard,
  ICreateAppPluginWidget,
  IUser,
  ModelService,
  IPluginPermissions,
  IPermissionsFlags,
	PluginInjectorService,
	IModuleWidget,
	PluginInjectorWidget,
	PluginCommunicationService
} from 'core-client/api';
import { SharedModule } from '../../shared/shared.module';
import { MatDialog } from '@angular/material/dialog';
import { SwiperOptions } from 'swiper/types';
import { SwiperDirective } from '../../shared/swiper.directive';
import { animate, state, style, transition, trigger } from '@angular/animations';

@Component({
    selector: 'app-main',
    templateUrl: './main.component.html',
    imports: [SharedModule, SwiperDirective],
    encapsulation: ViewEncapsulation.None,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    styleUrls: ['./main.component.scss'],
    animations: [
        trigger('hoverAnimation', [
            state('hidden', style({
                opacity: 0,
                visibility: 'hidden'
            })),
            state('visible', style({
                opacity: 1,
                visibility: 'visible'
            })),
            transition('hidden <=> visible', [
                animate('0.3s ease-in-out')
            ])
        ])
    ]
})
export class MainComponent implements OnInit, OnDestroy {
  @ViewChildren('widgetItems', { read: ElementRef }) widgetItems!: QueryList<ElementRef>;
  widgetInFullscreen: boolean = false;
  private readonly ngUnsubscribe$: Subject<void> = new Subject<void>();
  @Input() isMobileView: boolean = false;
  @Input() loggedUser: IUser | undefined;
  private permissions: IPluginPermissions[] = [];
  private widgetHeaderDisplayTimeout: ReturnType<typeof setTimeout> | null = null;

  // swiper config
  swiperConfig: SwiperOptions = {
    initialSlide: 0,
    spaceBetween: 10,
    pagination: true,
    navigation: false, // Show navigation arrows
    virtual: false
  };

  constructor(
    public modelService: ModelService,
    public dialog: MatDialog,
    public pluginInjectorService: PluginInjectorService,
    private pluginCommunicationService: PluginCommunicationService
  ) {}

  ngOnInit(): void {
    this.modelService._appDashboard.observeActiveAppDashboard$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(async (dashboard: IAppDashboard) => {
        if (this.loggedUser?._id === this.modelService._user.getUser()._id) {
          this.permissions = await this.pluginInjectorService.permissionService.getPermissions(
              dashboard,
              this.modelService._user.getUser()
          );

          // load dashboard widgets of user
          await this.loadDashboardWidgets(this.loggedUser);
        }
      })

    this.pluginInjectorService.widgetService.add$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(async (widgetItem: PluginInjectorWidget.IWidgetAddAction) => {
        // add new widget
        await this.addNewPluginWidget(widgetItem);
      })

    this.pluginInjectorService.widgetService.sizeChanged$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(async (appPluginWidget: IAppPluginWidget) => {
        // update appPluginWidget with new layout details in db
        await this.modelService._appPluginWidget.upsertOne(appPluginWidget);
      })

    this.pluginInjectorService.widgetService.edit$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((unlocked: boolean) => {
        this.pluginInjectorService.widgetService.options.resizable = { enabled: unlocked };
        this.pluginInjectorService.widgetService.options.draggable = { enabled: unlocked };

        // update angular-gridster2 api options
        if (this.pluginInjectorService.widgetService.options?.api?.optionsChanged) {
          this.pluginInjectorService.widgetService.options.api.optionsChanged();
        }
      })

    this.pluginInjectorService.widgetService.delete$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(async (_id: string) => {
        // delete appPluginWidget from db
        await this.modelService._appPluginWidget.deleteById(_id);
      })

    // listen for widget fullscreen change events
    document.addEventListener('fullscreenchange', this.onWidgetFullscreenChange.bind(this));
    document.addEventListener('webkitfullscreenchange', this.onWidgetFullscreenChange.bind(this));
    document.addEventListener('mozfullscreenchange', this.onWidgetFullscreenChange.bind(this));
    document.addEventListener('msfullscreenchange', this.onWidgetFullscreenChange.bind(this));
  }

  ngOnDestroy(): void {
    // cleanup widget fullscreen event listeners
    document.removeEventListener('fullscreenchange', this.onWidgetFullscreenChange.bind(this));
    document.removeEventListener('webkitfullscreenchange', this.onWidgetFullscreenChange.bind(this));
    document.removeEventListener('mozfullscreenchange', this.onWidgetFullscreenChange.bind(this));
    document.removeEventListener('msfullscreenchange', this.onWidgetFullscreenChange.bind(this));

    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();

    this.pluginInjectorService.widgetService.unsubscribeWidgets$();
  }

  private onWidgetFullscreenChange() {
    this.widgetInFullscreen = !!document.fullscreenElement;
    if (this.widgetInFullscreen && this.widgetHeaderDisplayTimeout == null) {
      this.widgetHeaderDisplayTimeout = setTimeout(() => {
        // after timeout set 'isHeaderHovered' flag of all widgets to 'false' in order to hide the header
        for (const widget of this.pluginInjectorService.widgetService.getWidgets()) {
          widget.isHeaderHovered = false;
        }
        this.clearWidgetHeaderDisplayTimeout();
      }, 3000);
    }
  }

  async removeWidgetItem($event: MouseEvent | TouchEvent, item: PluginInjectorWidget.IWidget, swiperContainerRef?: ElementRef): Promise<void> {
    $event.preventDefault();
    $event.stopPropagation();

    if (item._id) {
      // Extra additional action after removing the widget of a plugin
      if (item.pluginModule === 'MAP') {
        const ngsiSubscriptionWidgets =
            await this.modelService._ngsiSubscriptionWidget.listAll(`app_plugin_widget=${item.appPluginWidget._id}`);

        await this.pluginInjectorService.widgetService.executeAdditionalPluginAction(
            item,
            this.modelService,
            ngsiSubscriptionWidgets
        );
      }

      this.pluginInjectorService.widgetService.removeWidgetItemById(item._id);
    }

    if (swiperContainerRef) {
     swiperContainerRef.nativeElement.swiper.update();
    }
  }

  private async loadDashboardWidgets(user: IUser): Promise<void> {
    const widgets: PluginInjectorWidget.IWidget[] = [];
    const appPluginWidgets: IAppPluginWidget[] = await this.modelService._user.listAllAppPluginWidgets(user._id);
    const registerModuleWidgets: IModuleWidget[] = this.pluginInjectorService.widgetService.getModuleWidgets();

    for (const appPluginWidget of appPluginWidgets) {
      const appPlugin = await this.modelService._appPlugin.getById(appPluginWidget.app_plugin);

      this.addPluginWidgetExtraData(appPluginWidget);

      const widget: PluginInjectorWidget.IWidget = {
        _id: appPluginWidget._id,
        name: appPluginWidget.name,
        x: appPluginWidget.layout.position_x,
        y: appPluginWidget.layout.position_y,
        rows: appPluginWidget.layout.rows,
        cols: appPluginWidget.layout.columns,
        height: appPluginWidget.layout.height_px,
        width: appPluginWidget.layout.width_px,
        pluginId: ('plugin' in appPlugin) ? appPlugin.plugin : '',
        pluginModule: String(appPluginWidget.class),
        appPluginWidget: appPluginWidget,
        isHeaderHovered: false
      };

      // Finding the right widget of a module from a plugin
      const widgetModule = registerModuleWidgets.find(
          (moduleWidget: IModuleWidget) => moduleWidget.pluginId === widget.pluginId &&
              moduleWidget.pluginModule === widget.pluginModule
      );

      if (widgetModule) {
        widget.widgetPermissions = widgetModule.widgetPermissions;
      }

      if (this.pluginWidgetEnabled(widget)) {
        widgets.push(widget);
      }
    }

    this.pluginInjectorService.widgetService.setWidgets(widgets);
  }

  private async addNewPluginWidget(widgetItem: PluginInjectorWidget.IWidgetAddAction): Promise<void> {
    const widgetLayoutConfig: PluginInjectorWidget.IWidgetDefaultLayoutConfig = this.pluginInjectorService.widgetService.getDefaultLayoutConfig();
    const registerModuleWidgets: IModuleWidget[] = this.pluginInjectorService.widgetService.getModuleWidgets();

    const newAppPluginWidget: ICreateAppPluginWidget = {
      enabled: true,
      name: widgetItem.name,
      app_plugin: widgetItem.appPluginId,
      class: widgetItem.pluginModule,
      settings: {
        widgetHeader: {
          alwaysDisplayed: true,
          onHoverDisplayed: false,
          alwaysOff: false,
          transparent: false,
        },
        showWidgetTitle: true
      },
      layout: {
        position_x: widgetLayoutConfig.x,
        position_y: widgetLayoutConfig.y,
        columns: widgetLayoutConfig.cols,
        rows: widgetLayoutConfig.rows,
        width_px: widgetLayoutConfig.width,
        height_px: widgetLayoutConfig.height
      }
    };

    const appPluginWidget=
        await this.modelService._appPluginWidget.upsertOne(newAppPluginWidget);

    if ('_id' in appPluginWidget) {
      this.addPluginWidgetExtraData(appPluginWidget);

      const widget: PluginInjectorWidget.IWidget = {
        _id: appPluginWidget._id,
        name: appPluginWidget.name,
        x: appPluginWidget.layout.position_x,
        y: appPluginWidget.layout.position_y,
        rows: appPluginWidget.layout.rows,
        cols: appPluginWidget.layout.columns,
        height: appPluginWidget.layout.height_px,
        width: appPluginWidget.layout.width_px,
        pluginId: widgetItem.pluginId,
        pluginModule: String(appPluginWidget.class),
        appPluginWidget: appPluginWidget,
        isHeaderHovered: false
      };

      // Finding the right widget of a module from a plugin
      const widgetModule = registerModuleWidgets.find(
          (moduleWidget: IModuleWidget) => moduleWidget.pluginId === widget.pluginId &&
              moduleWidget.pluginModule === widget.pluginModule
      );

      if (widgetModule) {
        widget.widgetPermissions = widgetModule.widgetPermissions;
      }

      if (this.pluginWidgetEnabled(widget)) {
        this.pluginInjectorService.widgetService.addWidgetItem(widget);
      }
    }
  }

  private addPluginWidgetExtraData(appPluginWidget: IAppPluginWidget): void {
    if (appPluginWidget.extraData == null) {
      appPluginWidget.extraData = {};
    }
    // add extra data by widget class
    if (appPluginWidget.class === 'PHONE') {
      if (this.loggedUser != null) {
        appPluginWidget.extraData['astimaxAccount'] = this.loggedUser.astimaxAccount;
        appPluginWidget.extraData['userNames'] = this.loggedUser.userNames;
      }
    }
    // ...maybe other widget classes
  }

  private pluginEnabled(widget: PluginInjectorWidget.IWidget): boolean {
    return this.pluginInjectorService.getPluginById(widget.pluginId)?.enabled ?? false;
  }

  widgetSettingsEnabled(widget: PluginInjectorWidget.IWidget): boolean {
    const widgetPermissions = this.permissions.find(
        (pluginPermissions: IPluginPermissions) => pluginPermissions.plugin_id === widget.pluginId
    );

    if (widgetPermissions) {
      return this.pluginInjectorService.widgetService.widgetSettingsEnabled(
          widget,
          widgetPermissions
      );
    } else {
      return false;
    }
  }

  showWidgetHeader(widget: PluginInjectorWidget.IWidget): boolean {
    const widgetPermissions = this.permissions.find(
        (pluginPermissions: IPluginPermissions) => pluginPermissions.plugin_id === widget.pluginId
    );

    if (widgetPermissions) {
      const showWidgetHeader = this.pluginInjectorService.widgetService.showWidgetHeader(
          widget,
          widgetPermissions
      );

      if (!showWidgetHeader) {
        return showWidgetHeader;
      }

      return widget.appPluginWidget.settings.widgetHeader.alwaysDisplayed ||
          (widget.appPluginWidget.settings.widgetHeader.onHoverDisplayed && widget.isHeaderHovered);
    }

    return widget.appPluginWidget.settings.widgetHeader.alwaysDisplayed ||
        (widget.appPluginWidget.settings.widgetHeader.onHoverDisplayed && widget.isHeaderHovered);
  }

  private clearWidgetHeaderDisplayTimeout(): void {
    if (this.widgetHeaderDisplayTimeout != null) {
      clearTimeout(this.widgetHeaderDisplayTimeout);
      this.widgetHeaderDisplayTimeout = null;
    }
  }

  onWidgetHover(widget: PluginInjectorWidget.IWidget): void {
    widget.isHeaderHovered = true;
    if ((this.isMobileView || this.widgetInFullscreen) && this.widgetHeaderDisplayTimeout == null) {
      this.widgetHeaderDisplayTimeout = setTimeout(() => {
        widget.isHeaderHovered = false;
        this.clearWidgetHeaderDisplayTimeout();
      }, 3000);
    }
  }

  onWidgetHoverCancel(widget: PluginInjectorWidget.IWidget): void {
    widget.isHeaderHovered = false;
    this.clearWidgetHeaderDisplayTimeout();
  }

  onSwiperSlideChange(): void {
    // set the 'isHeaderHovered' flag to false on all widgets, since in mobile mode only one widget can be displayed at the same time and on slide change any hovering is canceled
    for (const widget of this.pluginInjectorService.widgetService.getWidgets()) {
      widget.isHeaderHovered = false;
    }
    this.clearWidgetHeaderDisplayTimeout();
  }

  private pluginWidgetEnabled(widget: PluginInjectorWidget.IWidget): boolean {
    return this.pluginEnabled(widget) && this.widgetEnabled(
        widget.pluginId,
        widget.pluginModule,
        widget.widgetPermissions
    );
  }

  private widgetEnabled(
      pluginId: string,
      pluginModule: string,
      widgetPermissions?: PluginInjectorWidget.IWidgetPermissions
  ): boolean {
    if (!widgetPermissions) {
      return false;
    }

    const pluginPermission = this.permissions.find(
        (plugin_permission: IPluginPermissions) => plugin_permission.plugin_id === pluginId
    );

    if (pluginPermission?.widget_permissions && widgetPermissions.widgetEnabledPermission?.permissionRef) {
      const hasModuleWidgetPermissions = Object.values(pluginPermission.widget_permissions).every(
          (flag: IPermissionsFlags | boolean) => typeof flag !== 'boolean'
      );

      if (!hasModuleWidgetPermissions &&
          pluginPermission.widget_permissions[widgetPermissions.widgetEnabledPermission.permissionRef]
      ) {
        return pluginPermission.widget_permissions[widgetPermissions.widgetEnabledPermission.permissionRef] as boolean;
      } else if (pluginModule && pluginPermission.widget_permissions[pluginModule.toLowerCase()]) {
        const permissionFlag = pluginPermission.widget_permissions[pluginModule.toLowerCase()] as IPermissionsFlags;
        return permissionFlag[widgetPermissions.widgetEnabledPermission.permissionRef];
      }
    }

    return false;
  }

  async toggleFullscreen(widgetIndex: number): Promise<void> {
    if (!document.fullscreenEnabled) {
      console.warn('Fullscreen API is not supported in this browser.');
      return;
    }

    // Get the widget at this index
    const widgets = this.pluginInjectorService.widgetService.getWidgets();
    const widget = widgets[widgetIndex];

    // Check if this is a phone widget and try to toggle video fullscreen first
    if (widget && widget.pluginModule === 'PHONE') {
      try {
        // Request phone widget to try toggling video fullscreen
        const handled = await this.pluginCommunicationService.request(
          'addboard',
          'astimax',
          'tryToggleVideoFullscreen',
          {}
        ).toPromise();

        // If phone widget handled video fullscreen, don't do widget fullscreen
        if (handled) {
          console.log('[Dashboard] Video fullscreen toggled by phone widget');
          return;
        }
      } catch (error) {
        console.warn('[Dashboard] Phone widget did not handle video fullscreen, falling back to widget fullscreen');
      }
    }

    // Fallback: Do normal widget fullscreen
    const element = this.widgetItems.toArray()[widgetIndex].nativeElement;
    if (!document.fullscreenElement) {
      if (element.requestFullscreen) {
        element.requestFullscreen().catch((error: any) => {
          console.error(`Error attempting to enable widget full-screen mode: ${error.message} (${error.name})`);
        });
      /* Firefox */
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen().catch((error: any) => {
          console.error(`Error attempting to enable widget full-screen mode for Firefox: ${error.message} (${error.name})`);
        });
      /* Chrome, Safari and Opera */
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen().catch((error: any) => {
          console.error(`Error attempting to enable widget full-screen mode for WebKit: ${error.message} (${error.name})`);
        });
      /* IE/Edge */
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen().catch((error: any) => {
          console.error(`Error attempting to enable widget full-screen mode for IE/Edge: ${error.message} (${error.name})`);
        });
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch((error: any) => {
          console.error(`Error attempting to exit widget full-screen mode: ${error.message} (${error.name})`);
        });
      /* Firefox */
      } else if ((document as any).mozCancelFullScreen) {
        (document as any).mozCancelFullScreen().catch((error: any) => {
          console.error(`Error attempting to exit widget full-screen mode for Firefox: ${error.message} (${error.name})`);
        });
      /* Chrome, Safari and Opera */
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen().catch((error: any) => {
          console.error(`Error attempting to exit widget full-screen mode for WebKit: ${error.message} (${error.name})`);
        });
      /* IE/Edge */
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen().catch((error: any) => {
          console.error(`Error attempting to exit widget full-screen mode for IE/Edge: ${error.message} (${error.name})`);
        });
      }
    }
  }

  widgetHeaderIsHovered(widget: PluginInjectorWidget.IWidget): boolean {
    return widget.appPluginWidget.settings.widgetHeader.onHoverDisplayed && widget.isHeaderHovered;
  }
}
